import { FeatureEnum, PermissionEnum } from '@/graphql/generated';
import { usePermissionChecker } from '@/hooks/usePermissionChecker';
import { LoaderFullscreen } from '@/components/layout/Loader';
import { NotFoundScreen } from '@/screens/public/NotFoundScreen';
import { FC, ReactNode, useEffect } from 'react';
import { useCurrentUser } from '@/providers/CurrentUserProvider';
import { isCoachUser } from '@/services/CompanyService';
import { AppRoutes } from '@/screens/AppRoutes';
import { useNavigate } from 'react-router';

interface FeatureGuardProps {
  feature: FeatureEnum;
  requiredPermission?: PermissionEnum;
  children: ReactNode;
}

export const FeatureGuard: FC<FeatureGuardProps> = ({
  feature,
  requiredPermission,
  children,
}) => {
  const { isGranted, loading } = usePermissionChecker();
  const { currentUser } = useCurrentUser();
  const isCoach = isCoachUser(currentUser);
  const navigate = useNavigate();
  const hasPermission = isGranted(feature, requiredPermission);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (hasPermission || !isCoach) {
      return;
    }

    navigate(AppRoutes.Home);
  }, [loading, isCoach, hasPermission]);

  if (loading) {
    return <LoaderFullscreen />;
  }

  if (!hasPermission) {
    return <NotFoundScreen />;
  }

  return <>{children}</>;
};
